import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import type { HTMLAttributes, PropsWithChildren } from "react"

import { cn } from "~/utils/helpers"

// Matches the TooltipProvider delayDuration in main.tsx, so arming lazily costs no extra wait.
const ARM_DELAY_MS = 100

const TooltipRoot = TooltipPrimitive.Root

const TooltipPortal = TooltipPrimitive.Portal

const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * Same enter/exit as the hover cards, so the two hover surfaces feel like one system.
 *
 * Only `delayed-open` animates. Radix reports `instant-open` when a tooltip opens inside the
 * provider's skipDelayDuration window — i.e. one was already on screen a moment ago — and
 * re-playing the grow on every hop across a row of triggers reads as flicker, not polish.
 */
const tooltipMotion = [
  "origin-(--radix-tooltip-content-transform-origin)",
  "data-[state=delayed-open]:overlay-enter data-[state=closed]:overlay-exit",
  "data-[side=bottom]:[--overlay-enter-y:-4px] data-[side=top]:[--overlay-enter-y:4px]",
].join(" ")

// Radix marks an Escape that dismisses a tooltip as handled (defaultPrevented), which would make
// keyboard handlers that yield to open layers ignore it. A tooltip is passive, so it shouldn't
// block them — this lets those handlers tell a tooltip's dismissal apart from a menu's.
const tooltipEscapes = new WeakSet<KeyboardEvent>()
export const isTooltipEscape = (event: KeyboardEvent) => tooltipEscapes.has(event)

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & { isBody?: boolean }
>(({ className, sideOffset = 4, isBody = false, onEscapeKeyDown, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    onEscapeKeyDown={(event) => {
      tooltipEscapes.add(event)
      onEscapeKeyDown?.(event)
    }}
    className={cn(
      tooltipMotion,
      "z-[100000] max-w-[300px] overflow-hidden rounded-[8px] bg-base px-2 py-1.5 paragraph-sm text-fg-base shadow-tooltip",
      isBody ? "px-3 py-2 rounded-[12px] text-left whitespace-pre-line" : "text-center",
      className,
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

const chain =
  <E,>(own: ((event: E) => void) | undefined, ours: (() => void) | undefined) =>
  (event: E) => {
    own?.(event)
    ours?.()
  }

type Props = {
  title?: string
  description?: string
  body?: React.ReactNode
  disableHoverableContent?: boolean
  className?: string
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  delayDuration?: number
  sideOffset?: number
  keepOpen?: boolean
  disabled?: boolean
  asChild?: boolean
}

const Tooltip = ({
  title,
  description,
  body,
  children,
  side = "top",
  align,
  className,
  disableHoverableContent,
  delayDuration,
  sideOffset = 4,
  keepOpen,
  disabled = false,
  asChild = true,
}: PropsWithChildren<Props>) => {
  // Radix's Root/Trigger/Popper tree is ~10 fibers per instance, which dominates render cost where
  // several tooltips mount per grid row. Render the trigger alone until the pointer rests on it,
  // then mount Radix already open — it never sees the pointerenter it missed.
  const [armed, setArmed] = useState(false)
  const [resting, setResting] = useState(false)
  const hasContent = Boolean(title || body)
  // Arming remounts the trigger inside Radix, which drops keyboard focus. Snapshot whether the
  // trigger was focused at arm time and hand focus back to the remounted one.
  const [focused, setFocused] = useState(false)
  const refocusRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (armed || !resting || !hasContent) return
    const timer = setTimeout(() => {
      refocusRef.current = focused
      setArmed(true)
    }, delayDuration ?? ARM_DELAY_MS)
    return () => clearTimeout(timer)
  }, [armed, resting, focused, hasContent, delayDuration])

  useLayoutEffect(() => {
    if (!armed || !refocusRef.current) return
    refocusRef.current = false
    triggerRef.current?.focus({ preventScroll: true })
  }, [armed])

  if (!keepOpen && !armed) {
    const enter = () => setResting(true)
    const leave = () => setResting(false)
    const focus = () => {
      setFocused(true)
      enter()
    }
    const blur = () => {
      setFocused(false)
      leave()
    }
    const handlers = (own: HTMLAttributes<HTMLElement> = {}) => ({
      onPointerEnter: chain(own.onPointerEnter, enter),
      onPointerLeave: chain(own.onPointerLeave, leave),
      onFocus: chain(own.onFocus, focus),
      onBlur: chain(own.onBlur, blur),
    })
    // Mirrors what Radix's Trigger renders: the child itself when asChild, else a bare button.
    return asChild && isValidElement<HTMLAttributes<HTMLElement>>(children) ? (
      cloneElement(children, handlers(children.props))
    ) : (
      <button type="button" {...handlers()}>
        {children}
      </button>
    )
  }

  return (
    <TooltipRoot
      open={keepOpen ? true : undefined}
      defaultOpen={!keepOpen}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      <TooltipTrigger ref={triggerRef} asChild={asChild}>
        {children}
      </TooltipTrigger>
      <TooltipPortal>
        {(title || body) && (
          <TooltipContent
            side={side}
            align={align}
            collisionPadding={4}
            sideOffset={sideOffset}
            className={className}
            isBody={body !== undefined}
            hidden={disabled}
          >
            {title
              ? title.split("\n").map((element, index, array) => (
                  <React.Fragment key={index}>
                    {element}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))
              : body}
            {description && (
              <div className="paragraph-sm text-fg-subtle">
                {description.split("\n").map((element, index, array) => (
                  <React.Fragment key={index}>
                    {element}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </TooltipContent>
        )}
      </TooltipPortal>
    </TooltipRoot>
  )
}

export { Tooltip }
